import React, { useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Share2, Search, MousePointerClick, X } from 'lucide-react';
import type { Scenario, StrategyTrade } from '@/services/strategyService';
import { TradeDetailPane } from './TradeDetailPane';
import {
  parseScenarioName, fmtNY, fmtHold, fmtHoldRich, fmtMoney, fmtPF, fmtShortDate,
  exitLabel, pnlColor,
} from './tradeViewerUtils';

type SortKey =
  | 'symbol' | 'direction' | 'entryTime' | 'entryPrice' | 'exitPrice'
  | 'realizedPnL' | 'realizedRMultiple' | 'exitReason' | 'holdingSeconds';

interface SortState { col: SortKey; dir: 'asc' | 'desc'; }

type Outcome = 'all' | 'winners' | 'losers';
type Side = 'all' | 'Long' | 'Short';

interface Props {
  scenario: Scenario;
  sharedMode?: boolean;
  onBack?: () => void;
  onShare?: () => void;
  selectedTradeIdx: number;
  onSelectTrade: (idx: number) => void;
}

// ── Stat tile ────────────────────────────────────────────────────

const StatTile: React.FC<{
  label: string; value: React.ReactNode; sub?: React.ReactNode; valueClass?: string;
}> = ({ label, value, sub, valueClass }) => (
  <Card className="px-3 py-2.5">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{label}</div>
    <div className={`text-lg font-bold tabular-nums leading-tight mt-0.5 ${valueClass ?? ''}`}>{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground tabular-nums truncate">{sub}</div>}
  </Card>
);

// ── Trade list ───────────────────────────────────────────────────

const COLS: { key: SortKey; label: string; align?: 'right' }[] = [
  { key: 'symbol', label: 'Symbol' },
  { key: 'direction', label: '' },
  { key: 'entryTime', label: 'Entry (ET)' },
  { key: 'realizedPnL', label: 'P&L', align: 'right' },
  { key: 'realizedRMultiple', label: 'R', align: 'right' },
  { key: 'exitReason', label: 'Exit' },
  { key: 'holdingSeconds', label: 'Hold', align: 'right' },
];

const ROW_GRID = 'grid-cols-[minmax(52px,1fr)_22px_88px_74px_46px_78px_44px]';

// ── Component ────────────────────────────────────────────────────

export const ScenarioDetail: React.FC<Props> = ({
  scenario, sharedMode, onBack, onShare, selectedTradeIdx, onSelectTrade,
}) => {
  const [sort, setSort] = useState<SortState>({ col: 'entryTime', dir: 'asc' });
  const [outcome, setOutcome] = useState<Outcome>('all');
  const [side, setSide] = useState<Side>('all');
  const [symbolFilter, setSymbolFilter] = useState('');
  const parentRef = useRef<HTMLDivElement | null>(null);

  const trades = scenario.trades ?? [];
  const st = scenario.statistics;
  const { run, entry, mgmt } = parseScenarioName(scenario.name);

  // Original index is the stable trade identity used in the URL (?trade=N),
  // so filtering/sorting must never renumber it.
  const idxByTradeId = useMemo(() => {
    const m = new Map<string, number>();
    trades.forEach((t, i) => m.set(t.id, i));
    return m;
  }, [trades]);

  const visible = useMemo(() => {
    const needle = symbolFilter.trim().toLowerCase();
    const filtered = trades.filter(t => {
      const pnl = t.realizedPnL || 0;
      if (outcome === 'winners' && pnl <= 0) return false;
      if (outcome === 'losers' && pnl >= 0) return false;
      if (side !== 'all' && t.direction !== side) return false;
      if (needle && !t.symbol.toLowerCase().includes(needle)) return false;
      return true;
    });
    filtered.sort((a, b) => {
      const av = (a as any)[sort.col];
      const bv = (b as any)[sort.col];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return filtered;
  }, [trades, outcome, side, symbolFilter, sort]);

  const rowVirtualizer = useVirtualizer({
    count: visible.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 30,
    overscan: 14,
  });

  const toggleSort = (col: SortKey) =>
    setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));

  // Only show a trade the current filters actually include. Selecting a loser
  // and then switching to Winners used to leave the loser charted with no row
  // highlighted — the detail pane contradicting the list next to it.
  const selectedTrade: StrategyTrade | null = (() => {
    if (selectedTradeIdx < 0) return null;
    const t = trades[selectedTradeIdx] ?? null;
    if (!t) return null;
    return visible.some(v => v.id === t.id) ? t : null;
  })();

  const winners = st?.winners ?? 0;
  const losers = st?.losers ?? 0;

  return (
    <div className="space-y-3">
      {/* ── Header ── */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          {!sharedMode && onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2 h-8 px-2">
              <ChevronLeft className="h-4 w-4 mr-1" /> All scenarios
            </Button>
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <h2 className="text-base font-semibold truncate" title={scenario.name}>{entry}</h2>
              {run && <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-primary/10 text-primary">{run}</span>}
              {mgmt && <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">{mgmt}</span>}
              {sharedMode && (
                <span className="rounded px-2 py-0.5 text-[10px] font-medium bg-blue-500/15 text-blue-400">
                  Shared · read-only
                </span>
              )}
            </div>
            {scenario.backtest && (
              <div className="text-[11px] text-muted-foreground tabular-nums mt-0.5">
                {fmtShortDate(scenario.backtest.startDate)} → {fmtShortDate(scenario.backtest.endDate)}
                {scenario.symbols?.length ? ` · ${scenario.symbols.length} symbols` : ''}
              </div>
            )}
          </div>
          {!sharedMode && onShare && (
            <Button variant="outline" size="sm" onClick={onShare} className="ml-auto">
              <Share2 className="h-4 w-4 mr-2" /> Share
            </Button>
          )}
        </div>
      </Card>

      {/* ── Summary tiles ── */}
      {st && (
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2">
          <StatTile label="Net P&L" value={fmtMoney(st.totalPnL)} valueClass={pnlColor(st.totalPnL)}
            sub={`${st.totalPnLPercent >= 0 ? '+' : ''}${st.totalPnLPercent.toFixed(2)}%`} />
          <StatTile label="Win rate" value={`${st.winRate.toFixed(1)}%`} sub={`${winners}W / ${losers}L`} />
          <StatTile label="Profit factor" value={fmtPF(st.profitFactor)}
            valueClass={st.profitFactor >= 1 ? 'text-emerald-500' : 'text-red-500'} />
          <StatTile label="Expectancy" value={fmtMoney(st.expectancy, true)} valueClass={pnlColor(st.expectancy)}
            sub="per trade" />
          <StatTile label="Avg R" value={st.avgRMultiple.toFixed(2)} valueClass={pnlColor(st.avgRMultiple)} />
          <StatTile label="Max DD" value={fmtMoney(-Math.abs(st.maxDrawdown))} valueClass="text-red-500"
            sub={`${st.maxDrawdownPercent.toFixed(1)}%`} />
          <StatTile label="Avg hold" value={fmtHoldRich(st.avgHoldingSeconds)} />
          <StatTile label="Trades" value={st.totalTrades}
            sub={`avg W ${fmtMoney(st.avgWin)} / L ${fmtMoney(-Math.abs(st.avgLoss))}`} />
        </div>
      )}

      {/* ── Trades + drill-down ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(380px,440px)_1fr] gap-3 lg:h-[calc(100vh_-_235px)] lg:min-h-[520px]">
        <Card className="flex flex-col min-h-0 max-h-[70vh] lg:max-h-none overflow-hidden">
          {/* Filters */}
          <div className="p-2 border-b space-y-2">
            <div className="flex items-center gap-1">
              {(['all', 'winners', 'losers'] as Outcome[]).map(o => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setOutcome(o)}
                  className={`rounded-md px-2 py-1 text-xs font-medium capitalize transition-colors ${
                    outcome === o
                      ? o === 'winners' ? 'bg-emerald-500/15 text-emerald-500'
                        : o === 'losers' ? 'bg-red-500/15 text-red-500'
                        : 'bg-primary/15 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {o}
                </button>
              ))}
              <span className="mx-1 h-4 w-px bg-border" />
              {(['all', 'Long', 'Short'] as Side[]).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSide(s)}
                  className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                    side === s ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {s === 'all' ? 'Both' : s}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={symbolFilter}
                onChange={(e) => setSymbolFilter(e.target.value)}
                placeholder="Filter by symbol…"
                className="w-full h-7 pl-7 pr-7 rounded-md border border-input bg-background text-xs
                           focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {symbolFilter && (
                <button
                  type="button"
                  onClick={() => setSymbolFilter('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear symbol filter"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground tabular-nums">
              {visible.length === trades.length
                ? `${trades.length} trades`
                : `${visible.length} of ${trades.length} trades`}
            </div>
          </div>

          {/* Sortable header */}
          <div className={`grid ${ROW_GRID} border-b bg-muted/30 text-[10px] font-semibold uppercase tracking-wide`}>
            {COLS.map(c => (
              <button
                key={c.key}
                type="button"
                onClick={() => toggleSort(c.key)}
                className={`px-1.5 py-1.5 hover:text-primary select-none truncate ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                title={`Sort by ${c.label || 'direction'}`}
              >
                {c.label}
                {sort.col === c.key && <span className="ml-0.5">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
              </button>
            ))}
          </div>

          {/* Virtualized rows */}
          <div ref={parentRef} className="overflow-auto flex-1 min-h-0">
            {visible.length === 0 ? (
              <div className="p-6 text-center text-xs text-muted-foreground">No trades match these filters.</div>
            ) : (
              <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
                {rowVirtualizer.getVirtualItems().map(vi => {
                  const t = visible[vi.index];
                  const origIdx = idxByTradeId.get(t.id) ?? -1;
                  const isSelected = origIdx === selectedTradeIdx;
                  const pnl = t.realizedPnL || 0;
                  const isWin = pnl > 0;
                  return (
                    <div
                      key={t.id}
                      onClick={() => onSelectTrade(origIdx)}
                      className={`grid ${ROW_GRID} items-center border-b text-[11px] cursor-pointer whitespace-nowrap
                                  border-l-2 transition-colors ${
                        isSelected
                          ? 'bg-primary/10 border-l-primary'
                          : 'border-l-transparent hover:bg-muted/50'
                      }`}
                      style={{
                        position: 'absolute', top: 0, left: 0, width: '100%',
                        height: `${vi.size}px`, transform: `translateY(${vi.start}px)`,
                      }}
                    >
                      <div className="px-1.5 font-medium truncate">{t.symbol}</div>
                      <div
                        className={`h-4 w-4 rounded-sm grid place-items-center text-[9px] font-bold ${
                          t.direction === 'Long' ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-500'}`}
                        title={t.direction}
                      >
                        {t.direction[0]}
                      </div>
                      <div className="px-1.5 tabular-nums text-muted-foreground">{fmtNY(t.entryTime)}</div>
                      <div className={`px-1.5 text-right tabular-nums font-semibold ${pnlColor(pnl)}`}>
                        {fmtMoney(pnl)}
                      </div>
                      <div className="px-1.5 text-right tabular-nums">
                        {t.realizedRMultiple != null ? t.realizedRMultiple.toFixed(1) : '-'}
                      </div>
                      <div className={`px-1.5 truncate ${isWin ? 'text-emerald-500/80' : 'text-red-500/80'}`}>
                        {exitLabel(t)}
                      </div>
                      <div className="px-1.5 text-right tabular-nums text-muted-foreground">{fmtHold(t.holdingSeconds)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        {/* Trade drill-down — scrolls as a column so the chart keeps a usable
            height instead of being squeezed by the facts/event cards. */}
        <div className="flex flex-col gap-2 min-h-0 lg:overflow-y-auto">
          {selectedTrade ? (
            <TradeDetailPane trade={selectedTrade} barsDisabled={sharedMode} />
          ) : (
            <Card className="flex-1 min-h-[300px] flex flex-col items-center justify-center gap-2 text-center px-6">
              <MousePointerClick className="h-7 w-7 text-muted-foreground/50" />
              <div className="text-sm font-medium">Pick a trade to inspect it</div>
              <div className="text-xs text-muted-foreground max-w-xs">
                The chart replays the exact bars the engine consumed, with entry, exit,
                stop and take-profit levels marked.
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScenarioDetail;
